<script setup>
import { computed, onMounted } from 'vue';
import { useWidgets } from '@store/widgets';
import { Select, Text, Button, IconUploader, Tooltip, Toggle, InputGroup, InputLabel, ProBadge } from '@components';
import { useTranslation } from '../../composables/useTranslation';

const $widget = useWidgets();
const { t } = useTranslation();
const whatsapp = computed(() => $widget.currentWidget?.config?.whatsapp || {})
const showWhatsAppDeliverySettings = computed(() => whatsapp.value.send_message_to_whatsapp !== false)
const isLocked = !window.formychat_admin_vars.is_premium || false

// Use modal store.
import { useModal } from '@store/modal'
const $modal = useModal();

const resetWhatsAppError = () => {
    $widget.errors.whatsapp_number = false;
}

// Agent 
const addAgent = () => {
    whatsapp.value.agents = whatsapp.value.agents || [];
    whatsapp.value.agents.push({ 
        name: t('agent_default_name', { number: whatsapp.value.agents.length + 1 }), 
        subtitle: '', 
        number: '', 
        country_code: window.formychat_country_code || '44' });
    $widget.errors.whatsapp_agents = false;
}

const deleteAgent = async (index) => {
    // Use modal to confirm deletion
    const confirmDelete = await $modal.prompt({
        title: t('delete_agent'),
        message: t('confirm_delete_agent'),
    });

    if (confirmDelete.ok) {
        whatsapp.value.agents.splice(index, 1);
    }
}

const moveAgent = (index, direction) => {
    const agents = whatsapp.value.agents;
    const newIndex = direction === 'up' ? index - 1 : index + 1;

    if (newIndex < 0 || newIndex >= agents.length) {
        return;
    }

    const agent = agents[index];
    agents.splice(index, 1);
    agents.splice(newIndex, 0, agent);
}


const initCountryCode = () => {
    if (!whatsapp.value.country_code || whatsapp.value.country_code.length === 0) {
        $widget.setCountryCode(window.formychat_country_code.toString() || '44');
    }
}
// on onMounted.
onMounted(async () => {
    // Set default agent mode
    whatsapp.value.agent_mode = whatsapp.value.agent_mode || 'single';
    whatsapp.value.destination_type = whatsapp.value.destination_type || 'phone';
    if (whatsapp.value.send_message_to_whatsapp === undefined) {
        whatsapp.value.send_message_to_whatsapp = true;
    }
    // Non-premium: always force the toggle back on (cannot be disabled without Ultimate).
    if (isLocked && !whatsapp.value.send_message_to_whatsapp) {
        whatsapp.value.send_message_to_whatsapp = true;
    }

    // Set up event listener first
    window.addEventListener('formychat_ip_loaded', initCountryCode);

    setTimeout(() => {
        initCountryCode();
    }, 300);

});
</script>
<template>
    <div class="w-full h-full flex flex-col gap-8">
        <!-- Name  -->
        <InputGroup>
            <div class="flex flex-col gap-2 max-w-md">
                <InputLabel semibold>{{ t('widget_name') }}</InputLabel>
                <Text v-model="$widget.currentWidget.name" :placeholder="t('widget_name')" />
            </div>
        </InputGroup>
        <InputGroup>
            <InputLabel semibold class="mb-2">{{ t('send_message_to_whatsapp_section') }}</InputLabel>
            <Toggle v-model="whatsapp.send_message_to_whatsapp" :locked="isLocked" :hints="t('send_message_to_whatsapp_hint')">
                {{ t('send_message_to_whatsapp') }}
            </Toggle>
        </InputGroup>
        <!-- Agents (Single / Multi) — only when sending to WhatsApp is enabled -->
        <InputGroup v-if="showWhatsAppDeliverySettings">
            <InputLabel semibold>{{ t('whatsapp_widget_display') }}</InputLabel>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-sm">
                <div v-for="(agentLabel, agent) in { single: t('widget_display_single'), agents: t('widget_display_multi_agents') }" :key="agent"
                    :class="['formychat-glass-card p-5 text-center group relative overflow-hidden cursor-pointer', whatsapp.agent_mode == agent ? 'formychat-glass-selected' : '', isLocked && agent !== 'single' ? 'formychat-locked' : '']"
                    @click.prevent="whatsapp.agent_mode = isLocked ? 'single' : agent">
                    <div class="absolute inset-0 bg-gradient-to-br from-indigo-500/0 to-indigo-500/0 group-hover:from-indigo-500/5 group-hover:to-indigo-500/5 transition-all duration-300 pointer-events-none"></div>
                    <transition name="bounce">
                        <div v-if="whatsapp.agent_mode == agent" class="absolute top-3 right-3 z-10">
                            <div class="formychat-checkmark-icon">
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                    <polyline points="20 6 9 17 4 12" />
                                </svg>
                            </div>
                        </div>
                    </transition>
                    <div v-if="isLocked && agent !== 'single'"
                        class="group-hover:opacity-100 transition duration-200 opacity-0 absolute inset-0 bg-white/80 backdrop-blur-[1px] rounded-xl flex items-center justify-center z-20">
                        <ProBadge />
                    </div>
                    <div class="relative z-10">
                        <div class="flex justify-center mb-3">
                            <div class="formychat-icon-circle shrink-0">
                                <svg v-if="agent === 'single'" xmlns="http://www.w3.org/2000/svg" class="w-6 h-6 text-indigo-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                                </svg>
                                <svg v-else xmlns="http://www.w3.org/2000/svg" class="w-6 h-6 text-indigo-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                                    <circle cx="9" cy="7" r="4" />
                                    <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
                                </svg>
                            </div>
                        </div>
                        <h3 class="text-base font-semibold text-slate-800">{{ agentLabel }}</h3>
                        <p class="text-xs text-slate-500 mt-1">{{ agent === 'single' ? (t('widget_display_single_hint') || 'Display widget for one agent.') : (t('widget_display_agents_hint') || 'Display widget for multiple agents.') }}</p>
                    </div>
                </div>
            </div>
        </InputGroup>
        <!-- Single WhatsApp Number / Group (hidden when "Send message to WhatsApp" is off) -->
        <InputGroup v-if="whatsapp.agent_mode === 'single' && showWhatsAppDeliverySettings">
            <InputLabel semibold>{{ t('whatsapp_destination') || 'Send to' }}</InputLabel>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5 max-w-2xl">
                <div v-for="(label, dest) in { phone: t('whatsapp_number') || 'Phone', group: t('whatsapp_group') || 'Group' }" :key="dest"
                    :class="['formychat-glass-card px-5 py-3 group relative overflow-hidden cursor-pointer', whatsapp.destination_type === dest ? 'formychat-glass-selected' : '', isLocked && dest === 'group' ? 'formychat-locked' : '']"
                    @click.prevent="whatsapp.destination_type = isLocked && dest === 'group' ? 'phone' : dest">
                    <div class="absolute inset-0 bg-gradient-to-br from-indigo-500/0 to-indigo-500/0 group-hover:from-indigo-500/5 group-hover:to-indigo-500/5 transition-all duration-300 pointer-events-none"></div>
                    <transition name="bounce">
                        <div v-if="whatsapp.destination_type === dest" class="absolute top-3 right-3 z-10">
                            <div class="formychat-checkmark-icon">
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                    <polyline points="20 6 9 17 4 12" />
                                </svg>
                            </div>
                        </div>
                    </transition>
                    <div v-if="isLocked && dest === 'group'"
                        class="group-hover:opacity-100 transition duration-200 opacity-0 absolute inset-0 bg-white/80 backdrop-blur-[1px] rounded-xl flex items-center justify-center z-20">
                        <ProBadge />
                    </div>
                    <div class="relative z-10 flex items-start gap-3 text-left">
                        <div class="formychat-icon-circle shrink-0 w-10 h-10">
                            <svg v-if="dest === 'phone'" xmlns="http://www.w3.org/2000/svg" class="w-6 h-6 text-indigo-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                            </svg>
                            <svg v-else xmlns="http://www.w3.org/2000/svg" class="w-6 h-6 text-indigo-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                                <circle cx="9" cy="7" r="4" />
                                <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
                            </svg>
                        </div>
                        <div class="flex flex-col gap-0.5 min-w-0 flex-1">
                            <h3 class="text-base font-semibold text-slate-800 leading-tight">{{ label }}</h3>
                            <p class="text-xs text-slate-500 leading-relaxed">{{ dest === 'phone' ? (t('whatsapp_number_hint') || 'Send messages to any phone number.') : (t('whatsapp_group_hint') || 'Send messages to a WhatsApp group.') }}</p>
                        </div>
                    </div>
                </div>
            </div>
            <!-- Phone number fields -->
            <div v-if="whatsapp.destination_type === 'phone'" class="flex flex-col gap-2 max-w-md">
                <InputLabel semibold>{{ t('whatsapp_number_url_label') || 'WhatsApp Number' }}</InputLabel>
                <div class="formychat-glass-card formychat-wa-phone-row p-3 relative flex items-center gap-2">
                    <Select :options="$widget.contents.countries" v-model="whatsapp.country_code" :cursor="false"
                        class="w-24 shrink-0" size="w-24" :placeholder="t('select')" white teleport
                        dropdown-anchor-selector=".formychat-wa-phone-row" :dropdown-min-width="360" :excerpt="120" />
                    <div class="flex-1 relative min-w-0">
                        <Text name="whatsapp_number" v-model="whatsapp.number" @keydown="resetWhatsAppError" type="text"
                            :placeholder="t('whatsapp_number_placeholder')"
                            :error="!$widget.isValidPhone(whatsapp.number) || $widget.errors.whatsapp_number"
                            white />
                        <transition name="bounce">
                            <div v-if="!$widget.isValidPhone(whatsapp.number) || $widget.errors.whatsapp_number"
                                class="absolute w-4 text-red-500 right-3 top-3 flex items-center gap-2 transition"
                                :class="{ 'bounce-enter-active': $widget.errors.whatsapp_number }">
                                <svg xmlns="http://www.w3.org/2000/svg" class="fill-current" viewBox="0 0 16 16">
                                    <path d="M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5m.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2" />
                                </svg>
                            </div>
                        </transition>
                    </div>
                </div>
                <span class="text-xs text-slate-500 leading-relaxed">{{ t('whatsapp_number_input_hint') || 'Enter your WhatsApp number with country code' }}</span>
            </div>
            <!-- Group invite link -->
            <div v-else-if="whatsapp.destination_type === 'group' && !isLocked" class="flex flex-col gap-2 max-w-md">
                <InputLabel semibold>{{ t('whatsapp_group_url_label') || 'WhatsApp Group URL or Code' }}</InputLabel>
                <div class="formychat-glass-card p-3 relative">
                    <Text v-model="whatsapp.group_invite_code" type="text" name="group_invite_code"
                        @keydown="$widget.errors.group_invite_code = false"
                        :error="$widget.errors.group_invite_code"
                        :placeholder="t('whatsapp_group_invite_placeholder') || 'Paste group invite link or code (e.g. https://chat.whatsapp.com/ABC123)'"
                        white />
                </div>
                <span class="text-xs text-slate-500 leading-relaxed">{{ t('whatsapp_group_invite_hint') || 'Paste the full WhatsApp group invite URL or just the invite code' }}</span>
            </div>
        </InputGroup>
        <!-- When Agent Mode is ON  -->
        <div class="flex flex-col gap-4" v-if="whatsapp.agent_mode === 'agents' && showWhatsAppDeliverySettings">
            <!-- Agents  -->
            <div v-if="whatsapp.agents && whatsapp.agents.length" v-for="(agent, index) in whatsapp.agents" :key="index"
                class="flex flex-col gap-4">
                <div class="bg-slate-50/80 p-4 rounded-xl border-2 border-slate-200 flex flex-col gap-4 relative shadow-sm">
                    <!-- delete  -->
                    <div class="absolute z-10 right-3 top-3 child:cursor-pointer flex items-center gap-3">
                        <Tooltip v-if="index > 0">
                            <svg xmlns="http://www.w3.org/2000/svg" @click.prevent="moveAgent(index, 'up')"
                                class="fill-current rotate-180 w-3 opacity-60 hover:opacity-100 transition"
                                viewBox="0 0 16 16">
                                <path fill-rule="evenodd"
                                    d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708" />
                            </svg>
                            <template #content>{{ t('move_up') }}</template>
                        </Tooltip>
                        <Tooltip v-if="index < whatsapp.agents.length - 1">
                            <svg xmlns="http://www.w3.org/2000/svg" @click.prevent="moveAgent(index, 'down')"
                                class="fill-current w-3 opacity-60 hover:opacity-100 transition" viewBox="0 0 16 16">
                                <path fill-rule="evenodd"
                                    d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708" />
                            </svg>
                            <template #content>{{ t('move_down') }}</template>
                        </Tooltip>
                        <svg @click="deleteAgent(index)" xmlns="http://www.w3.org/2000/svg"
                            class="fill-current w-4 hover:opacity-100 transition hover:text-red-500"
                            viewBox="0 0 16 16">
                            <path
                                d="M2.5 1a1 1 0 0 0-1 1v1a1 1 0 0 0 1 1H3v9a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2V4h.5a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1H10a1 1 0 0 0-1-1H7a1 1 0 0 0-1 1zm3 4a.5.5 0 0 1 .5.5v7a.5.5 0 0 1-1 0v-7a.5.5 0 0 1 .5-.5M8 5a.5.5 0 0 1 .5.5v7a.5.5 0 0 1-1 0v-7A.5.5 0 0 1 8 5m3 .5v7a.5.5 0 0 1-1 0v-7a.5.5 0 0 1 1 0" />
                        </svg>
                    </div>
                    <!-- Profile picture. -->
                    <InputGroup>
                        <IconUploader v-model="agent.avatar" v-model:custom="agent.custom_avatar"
                            :default="Image('whatsapp-circle.svg')" />
                    </InputGroup>
                    <!-- Name  -->
                    <InputGroup>
                        <InputLabel>{{ t('agent_name') }}</InputLabel>
                        <Text :placeholder="t('agent_name_example')" v-model="agent.name" />
                    </InputGroup>
                    <!-- Subtitle  -->
                    <InputGroup>
                        <InputLabel>{{ t('subtitle_designation') }}</InputLabel>
                        <Text :placeholder="t('subtitle_example')" v-model="agent.subtitle" />
                    </InputGroup>
                    <!-- WhatsApp Number  -->
                    <InputGroup>
                        <InputLabel>{{ t('agent_whatsapp_number') }}</InputLabel>
                        <div class="formychat-wa-phone-row flex items-center gap-3">
                            <Select :options="$widget.contents.countries" v-model="agent.country_code" :cursor="false"
                                class="w-24 justify-center text-center shrink-0" size="w-24" white teleport
                                :placeholder="t('select')" dropdown-anchor-selector=".formychat-wa-phone-row"
                                :dropdown-min-width="360" :excerpt="120" />
                            <div class="w-full relative">
                                <Text v-model="agent.number" type="text"
                                @keydown="$widget.errors['agent_number_'+index] = false" :error="!$widget.isValidPhone(agent.number) || ($widget.errors['agent_number_'+index])" 
                                :placeholder="t('whatsapp_number_placeholder')" :name="'agent_number_' + index"/>
                                <transition name="bounce">
                                    <div v-if="$widget.errors['agent_number_' + index]"
                                        class="absolute w-4 text-red-500 right-3 top-3 flex items-center gap-2 transition"
                                        :class="{ 'bounce-enter-active': $widget.errors.whatsapp_number }">
                                        <svg xmlns="http://www.w3.org/2000/svg" class="fill-current"
                                            viewBox="0 0 16 16">
                                            <path
                                                d="M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5m.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2" />
                                        </svg>
                                    </div>
                                </transition>
                            </div>
                        </div>
                    </InputGroup>
                </div>
            </div>
            <!-- No agent found  -->
            <!-- <div v-else class="bg-indigo-50 border border-indigo-200 px-6 py-4 rounded text-indigo-500">Create your first agent</div> -->
            <!-- Add agent  -->
            <!-- When no agents  -->
            <transition name="fade">
                <div v-if="$widget.errors.whatsapp_agents"
                    class="text-red-500 text-sm py-4 flex items-center justify-between px-6 rounded bg-red-50 border border-red-200">
                    {{ t('add_one_agent_error') }} <span class="cursor-pointer px-3 py-1 bg-red-300 rounded"
                        @click="$widget.errors.whatsapp_agents = false">x</span>
                </div>
            </transition>
            <div> <Button outline class="px-2 h-8" @click="addAgent"><svg xmlns="http://www.w3.org/2000/svg" width="14"
                        height="14" fill="currentColor" class="bi bi-plus-lg" viewBox="0 0 16 16">
                        <path fill-rule="evenodd"
                            d="M8 2a.5.5 0 0 1 .5.5v5h5a.5.5 0 0 1 0 1h-5v5a.5.5 0 0 1-1 0v-5h-5a.5.5 0 0 1 0-1h5v-5A.5.5 0 0 1 8 2" />
                    </svg> {{ t('add_new_agent') }}</Button> </div>
        </div>
        <!-- Redirect to whatsapp on clicking agent  -->
        <div v-if="showWhatsAppDeliverySettings" class="flex items-start gap-8 flex-col">
            <h3 class="font-semibold text-slate-800">{{ t('behaviors') }}</h3>
            <Toggle v-model="whatsapp.new_tab"
                :hints="t('open_whatsapp_new_tab_hint')">
                {{ t('open_whatsapp_new_tab') }}</Toggle>
            <Toggle v-model="whatsapp.web_version" locked :hints="t('whatsapp_web_version_hint')">{{ t('whatsapp_web_version') }}</Toggle>
            <div v-if="whatsapp.agent_mode === 'agents'" class="flex flex-col gap-3">
                <InputLabel>{{ t('on_click_agent') }}</InputLabel>
                <div class="flex flex-col gap-2.5 child:cursor-pointer">
                    <label class="flex items-center gap-4 group"
                        v-for="(label, action) in { show_form: t('load_selected_form'), redirect: t('redirect_whatsapp_directly') }"
                        @click.prevent="(isLocked && action == 'redirect') ? null : whatsapp.on_click_agent = action"
                        :class="{ 'text-indigo-500': whatsapp.on_click_agent == action, }">
                        <div class="w-5 h-5 bg-white border-[5px] rounded-full  transition" :class="{
                            'border-indigo-500': whatsapp.on_click_agent == action,
                            'border-gray-100': whatsapp.on_click_agent !== action,
                            'group-hover:border-indigo-500': whatsapp.on_click_agent !== action && !isLocked,
                            'formychat-locked': isLocked && action === 'redirect'
                        }"><span></span></div> <span :class="{
                            'opacity-60 formychat-locked': isLocked && action === 'redirect'
                        }">{{ label }}</span>
                        <ProBadge v-if="action === 'redirect'" />
                    </label>
                </div>
            </div>
        </div>
    </div>
</template>